import React from 'react';
import { colors } from '../../styles/tokens';
import { useEsMovil } from '../../hooks/useEsMovil';
import type { TiendaBodega } from '../../types/indicadores.types';
import { fmtInt, fmtPct, mmss, ring } from '../../utils/format';
import { GLOSARIO, semaforo } from '../../utils/glosario';

export interface BodegaCardProps {
  bodega: TiendaBodega;
  /**
   * `true` (default) = la vista está en vivo y el bloque es el del día. Con un
   * periodo elegido la API calcula sobre el rango, y el título decía «de hoy»
   * igual: alguien podía leer las entregas de un mes como las de una mañana.
   */
  enVivo?: boolean;
}

/** Circunferencia del anillo SVG (`r=44`, igual que el design handoff línea 222). */
const RING_CIRC = 2 * Math.PI * 44;

/**
 * Card "Bodega · respuesta de hoy" / "… del periodo" (Mi tienda): recreado de
 * `Tablero Colectivo Moda.dc.html` (líneas 215-229) — icono + "Bodeguero /
 * Usuario genérico de bodega", anillo grande (r=44) con el % en el centro, y
 * 2 stats chip (Solicitudes atendidas / Tiempo prom.) debajo.
 *
 * ⚠️ `bodega.pctSla` es atendidas ÷ solicitudes (A ÷ S, `get-tienda.use-case`),
 * la MISMA cifra que el medidor «Atendidas por bodeguero» de Global. El anillo
 * decía «SLA EN TIEMPO · entregadas dentro de tiempo», que no es lo que mide, y
 * la coloreaba con umbrales propios (93/88). Desde el 12-oct lleva el rótulo y
 * el semáforo del glosario (`semaforo('atendidasBodega')`, 95/90). Llega como
 * FRACCIÓN 0..1 (o `null`); el trazo (`ring`) ya recorta a [0,100] y trata
 * `null` como 0.
 */
const BodegaCard: React.FC<BodegaCardProps> = ({ bodega, enVivo = true }) => {
  const esMovil = useEsMovil();
  // El anillo escala (mismo `viewBox`, otro tamaño de pintado): 150 px en
  // escritorio, 132 en teléfono, donde la card es lo único de su fila y
  // conviene que el bloque completo entre sin empujar el resto hacia abajo.
  const anilloPx = esMovil ? 132 : 150;
  const slaPct0a100 = bodega.pctSla === null || bodega.pctSla === undefined ? null : bodega.pctSla * 100;
  const color = semaforo('atendidasBodega', bodega.pctSla);

  return (
    /* ⚠️ El título va DENTRO de la tarjeta, como el de `GraficoHora`.
       Estaba fuera, sobre el fondo gris, y como las dos comparten fila el
       borde superior de la tarjeta de bodega arrancaba 32 px por debajo del
       de la gráfica: medido a 1920 px, celdas alineadas en y=606 y tarjetas
       en 606 contra 638. Nada desbordaba y ninguna comprobación lo veía; se
       leía como una fila torcida. */
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        data-testid="bodega-card"
        style={{
          flex: 1,
          background: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: 16,
          padding: esMovil ? '16px 14px' : '20px 18px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 1px 2px rgba(20,28,54,.05)',
        }}
      >
        <h3 style={{ margin: '0 0 14px', fontSize: 14.5, fontWeight: 800 }}>
          {enVivo ? 'Bodega · respuesta de hoy' : 'Bodega · respuesta del periodo'}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: esMovil ? 0 : undefined }}>
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              background: colors.texto.strong,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
            }}
          >
            <svg
              width={20}
              height={20}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth={1.9}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9l9-6 9 6v10a1 1 0 0 1-1 1h-4v-7H8v7H4a1 1 0 0 1-1-1z" />
            </svg>
          </span>
          {/* `minWidth: 0`: sin él este hijo del flex no se deja encoger y
              "Usuario genérico de bodega" empujaría la card fuera de los
              390 px de la pantalla. */}
          <div style={{ minWidth: esMovil ? 0 : undefined }}>
            <div style={{ fontWeight: 800, fontSize: 15, lineHeight: 1 }}>Bodeguero</div>
            <div style={{ fontSize: esMovil ? 12 : 11, color: colors.texto.quaternary, fontWeight: 600, marginTop: 2 }}>
              Usuario genérico de bodega
            </div>
          </div>
        </div>

        <div
          style={{
            position: 'relative',
            width: anilloPx,
            height: anilloPx,
            maxWidth: '100%',
            alignSelf: 'center',
            margin: esMovil ? '12px 0 6px' : '14px 0 6px',
          }}
        >
          <svg width={anilloPx} height={anilloPx} viewBox="0 0 100 100">
            <circle cx={50} cy={50} r={44} fill="none" stroke={colors.ringTrack} strokeWidth={9} />
            <circle
              cx={50}
              cy={50}
              r={44}
              fill="none"
              stroke={color}
              strokeWidth={9}
              strokeLinecap="round"
              strokeDasharray={ring(slaPct0a100, RING_CIRC)}
              transform="rotate(-90 50 50)"
            />
          </svg>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            <span
              style={{
                fontSize: esMovil ? 28 : 31,
                fontWeight: 800,
                color: colors.texto.primary,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {fmtPct(bodega.pctSla, 0)}
            </span>
            <span
              style={{
                // 9,5 px no se lee en un teléfono: la etiqueta del anillo sube
                // al piso de 11 px que nos pusimos para etiquetas.
                fontSize: esMovil ? 11 : 9.5,
                fontWeight: 700,
                color: colors.texto.quaternary,
                marginTop: 5,
                letterSpacing: '.06em',
              }}
            >
              ATENDIDAS
            </span>
          </div>
        </div>

        <div style={{ textAlign: 'center', fontSize: 12, color: colors.texto.tertiary, fontWeight: 600, marginBottom: esMovil ? 12 : 14 }}>
          {GLOSARIO.atendidasBodega.rotulo} · {GLOSARIO.atendidasBodega.nota?.toLowerCase()}
        </div>

        {/* Dos cifras cortas: se quedan de a dos incluso en teléfono (la regla
            admite dos columnas cuando la tarjeta es sólo un número), con
            `minWidth: 0` para que el flex sí pueda encogerlas. */}
        <div style={{ display: 'flex', gap: esMovil ? 10 : 8 }}>
          <div style={{ flex: 1, minWidth: esMovil ? 0 : undefined, background: colors.chipBg, borderRadius: 10, padding: esMovil ? '11px 12px' : '11px 13px' }}>
            <div
              style={{
                fontSize: esMovil ? 11 : 10,
                color: colors.texto.quaternary,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '.03em',
              }}
            >
              {GLOSARIO.atendidas.rotulo}
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
              {fmtInt(bodega.entregas)}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: esMovil ? 0 : undefined, background: colors.chipBg, borderRadius: 10, padding: esMovil ? '11px 12px' : '11px 13px' }}>
            <div
              style={{
                fontSize: esMovil ? 11 : 10,
                color: colors.texto.quaternary,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '.03em',
              }}
            >
              Tiempo prom.
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
              {mmss(bodega.tiempoPromSeg)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BodegaCard;
